import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Status, StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatMetric, pickPrimaryMetric } from "@/lib/metrics";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { CompareDrawer } from "./compare-drawer";
import { useRevisionAction } from "./mutations";
import { type RevisionSummary, useModel, useRevisions } from "./queries";

function revStatus(s: RevisionSummary["status"]): Status {
  switch (s) {
    case "active":
      return "active";
    case "archived":
      return "archived";
    case "discarded":
      return "discarded";
    case "failed":
      return "failed";
    case "training":
      return "running";
    case "draft":
      return "draft";
    default:
      return "archived";
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

type ConfirmTarget = { action: "activate" | "archive" | "discard"; rev: number };

export function RevisionsTable({ modelId }: { modelId: string }) {
  const { data } = useRevisions(modelId);
  const { data: model } = useModel(modelId);
  const [selected, setSelected] = useState<number[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmTarget | null>(null);

  const activate = useRevisionAction(modelId, "activate");
  const archive = useRevisionAction(modelId, "archive");
  const discard = useRevisionAction(modelId, "discard");

  const toggle = (n: number) =>
    setSelected((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n].slice(-2)));

  function handleConfirm() {
    if (!confirm) return;
    const mutation =
      confirm.action === "activate" ? activate : confirm.action === "archive" ? archive : discard;
    mutation.mutate(confirm.rev, { onSettled: () => setConfirm(null) });
  }

  const isBusy = activate.isPending || archive.isPending || discard.isPending;

  const revNo = confirm?.rev ?? 0;
  const dialogTitle =
    confirm?.action === "activate"
      ? `Activate revision #${revNo}?`
      : confirm?.action === "archive"
        ? `Archive revision #${revNo}?`
        : `Discard revision #${revNo}?`;

  const dialogDescription =
    confirm?.action === "activate"
      ? "This will make the selected revision the active inference endpoint."
      : confirm?.action === "archive"
        ? "The revision will be archived and no longer used for inference."
        : "This will permanently delete the artifact from object storage. This cannot be undone.";

  return (
    <div className="rounded-lg border border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <span className="text-xs font-medium">Revisions ({data?.items.length ?? 0})</span>
        <Button
          size="sm"
          variant="default"
          disabled={selected.length !== 2}
          onClick={() => setCompareOpen(true)}
        >
          Compare ({selected.length}/2)
        </Button>
      </div>
      <div className="divide-y divide-border">
        {data?.items.map((r) => (
          <div key={r.revision_no} className="flex items-center gap-3 px-4 py-2.5">
            <input
              type="checkbox"
              checked={selected.includes(r.revision_no)}
              onChange={() => toggle(r.revision_no)}
              disabled={r.status === "failed" || r.status === "discarded"}
              aria-label={`Select revision ${r.revision_no}`}
              title={
                r.status === "failed"
                  ? "Failed revisions have no metrics"
                  : r.status === "discarded"
                    ? "Discarded revision"
                    : undefined
              }
            />
            <span className="mono text-xs w-14">#{r.revision_no}</span>
            <StatusDot status={revStatus(r.status)} />
            {(() => {
              const evalMetric =
                typeof r.training_summary?.eval_metric === "string"
                  ? r.training_summary.eval_metric
                  : undefined;
              const primary = pickPrimaryMetric(r.metrics, evalMetric);
              if (!primary) {
                return <span className="text-xs text-fg-dim flex-1 mono">—</span>;
              }
              return (
                <span className="text-xs text-fg-muted flex-1 mono">
                  {primary.name}{" "}
                  <span className="text-fg">{formatMetric(primary.name, primary.value)}</span>
                </span>
              );
            })()}
            <span className="text-xs text-fg-dim mono">{formatDate(r.created_at)}</span>
            {r.decision_threshold != null && (
              <span
                title={
                  r.decision_threshold_calibrated_at
                    ? `Calibrated ${new Date(r.decision_threshold_calibrated_at).toLocaleString()}`
                    : "Calibrated threshold"
                }
                className="rounded border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-fg-muted"
              >
                thr={r.decision_threshold.toFixed(2)}
                {r.decision_threshold_metric ? (
                  <span className="text-fg-dim"> · {r.decision_threshold_metric}</span>
                ) : null}
              </span>
            )}
            {(() => {
              // Failed/discarded revs have no artifact → no actions.
              // Active can only be archived. Other usable statuses can be
              // activated or discarded.
              const canActivate = r.status === "archived" || r.status === "draft";
              const canArchive = r.status === "active";
              const canDiscard = canActivate; // same set: archived or draft
              const actions = [
                canActivate && { key: "activate", label: "Activate" },
                canArchive && { key: "archive", label: "Archive" },
                canDiscard && { key: "discard", label: "Discard", danger: true },
              ].filter(Boolean) as {
                key: "activate" | "archive" | "discard";
                label: string;
                danger?: boolean;
              }[];
              if (actions.length === 0) return <div className="w-9" />;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Revision ${r.revision_no} actions`}
                    >
                      <MoreHorizontal size={14} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {actions.map((a) => (
                      <DropdownMenuItem
                        key={a.key}
                        variant={a.danger ? "danger" : undefined}
                        onSelect={() => setConfirm({ action: a.key, rev: r.revision_no })}
                      >
                        {a.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}
          </div>
        ))}
      </div>
      <CompareDrawer
        modelId={modelId}
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        a={selected[0] ?? null}
        b={selected[1] ?? null}
      />
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => {
          if (!o) setConfirm(null);
        }}
        title={dialogTitle}
        description={dialogDescription}
        confirmLabel={
          confirm?.action === "activate"
            ? "Activate"
            : confirm?.action === "archive"
              ? "Archive"
              : "Discard"
        }
        onConfirm={handleConfirm}
        requireText={confirm?.action === "discard" ? (model?.name ?? undefined) : undefined}
        tone={confirm?.action === "discard" ? "danger" : "default"}
        busy={isBusy}
      />
    </div>
  );
}
